<template>
    <page title="newUserRoleTitle">
        <template #pageContainer>
            <v-alert v-if="isNoAdmin" type="info">
                {{ $t(`text.newRoleRequestAdminInfo`) }}
            </v-alert>
            <form>
                <v-autocomplete outlined dense
					hide-details="auto"
                    :disabled="loading || !anyUser"
                    :readonly="hasOnlyOneUser"
                    v-model="user.selected"
                    :items="user.items"
                    :error-messages="userErrors"
                    required
                    :label="$t('text.user')"
                    :loading="userLoading"
                    @input="$v.user.selected.$touch()"
                    @blur="$v.user.selected.$touch()">
                </v-autocomplete>

                <v-autocomplete outlined dense
					hide-details="auto"
                    :disabled="loading || !anyOrg"
                    :readonly="hasOnlyOneOrganization"
                    v-model="organization.selected"
                    :items="organization.items"
                    :error-messages="organizationErrors"
                    required
                    :label="$t('text.organization')"
                    :loading="orgLoading"
                    @input="$v.organization.selected.$touch()"
                    @blur="$v.organization.selected.$touch()">
                </v-autocomplete>

                <v-autocomplete outlined dense
					hide-details="auto"
                    multiple
                    :disabled="loading || !anyRole"
                    :readonly="hasOnlyOneRole"
                    v-model="role.selected"
                    :items="roles"
                    :label="$t('text.role')"
                    :error-messages="roleErrors"
                    required
                    :loading="roleLoading"
                    @input="$v.role.selected.$touch()"
                    @blur="$v.role.selected.$touch()">
                </v-autocomplete>

                <v-btn
                    class="mr-4"
                    :disabled="$v.$invalid || loading"
                    @click="create">
                    {{ $t(`text.createButton`) }}
                </v-btn>

                <v-btn
                    :disabled="loading"
                    class="mr-4"
                    @click="back">
                    {{ $t(`text.backButton`) }}
                </v-btn>
            </form>

            <spinner :loading="loading"></spinner>
        </template>
    </page>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';

import Page from '../general/Page.vue';
import Spinner from '../general/Spinner.vue';

import userRoleActions from '../store/actions/userRole';
import userActions from '../store/actions/user';
import organizationActions from '../store/actions/organization';
import namespace from '../store/namespace';
import role from '../helpers/role';

import modelState from '../mixins/modelState';

export default {
	name: 'NewUserRole',
	mixins: [modelState],
	components: {
		Page,
		Spinner,
	},
	validations: {
		organization: {
			selected: {
				required,
			},
		},
		role: {
			selected: {
				required,
			},
		},
		user: {
			selected: {
				required,
			},
		},
	},
	data() {
		return {
			organization: {
				items: [],
				selected: '',
			},
			role: {
				items: [],
				selected: [],
			},
			user: {
				items: [],
				selected: '',
			},
			orgLoading: false,
			roleLoading: false,
			userLoading: false,
			loading: false,
		};
	},
	computed: {
		...mapGetters(namespace.ORGANIZATION, ['Organization']),
		...mapGetters(namespace.USER, ['User']),
		...mapGetters(namespace.USERROLE, ['Links', 'Entities']),
		organizationErrors() {
			const errors = [];

			if (!this.$v.organization.selected.$dirty) return errors;

			const apiErrors = this.validateProperty('OrganizationId');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.organization.selected.required && errors.push(this.$i18n.t('text.errorOrganizationRequired'));

			return errors;
		},
		roleErrors() {
			const errors = [];

			if (!this.$v.role.selected.$dirty) return errors;

			const apiErrors = this.validateProperty('RoleId');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.role.selected.required && errors.push(this.$i18n.t('text.errorRoleRequired'));

			return errors;
		},
		userErrors() {
			const errors = [];

			if (!this.$v.user.selected.$dirty) return errors;

			const apiErrors = this.validateProperty('UserId');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.user.selected.required && errors.push(this.$i18n.t('text.errorUserRequired'));

			return errors;
		},
		roles() {
			return this.role.items;
		},
		isNoAdmin() {
			return !!this.role.items
                && this.role.items.length === 1
                && this.role.items[0].code === role.OrgAdmin;
		},
		hasOnlyOneOrganization() {
			return this.organization.items.length === 1;
		},
		hasOnlyOneRole() {
			return this.role.items.length === 1;
		},
		hasOnlyOneUser() {
			return this.user.items.length === 1;
		},
		anyOrg() {
			return this.organization.items.length > 0;
		},
		anyUser() {
			return this.user.items.length > 0;
		},
		anyRole() {
			return this.role.items.length > 0;
		},
	},
	async mounted() {
		await this.getUsers();
	},
	watch: {
		'organization.selected': async function () {
			await this.getRoles();
		},
		'user.selected': async function () {
			await this.getOrganizations();
		},
	},
	methods: {
		...mapActions(namespace.USERROLE, [
			userRoleActions.CREATE_NEW_USER_ROLE,
		]),
		...mapActions(namespace.USER, [
			userActions.GET_USERS,
		]),
		...mapActions(namespace.ORGANIZATION, [
			organizationActions.GET_BRANCHES,
			organizationActions.GET_ORGANIZATION_ROLES,
		]),
		async create() {
			this.loading = true;
			this.$v.$touch();

			if (!this.$v.$invalid) {
				const link = this.Links.find(x => x.rel === 'new');

				if (link) {
					const newUserRole = {
						roleIds: this.role.selected,
						organizationId: this.organization.selected,
						userId: this.user.selected,
					};

					try {
						this.modelState = {};

						await this.CREATE_NEW_USER_ROLE({ newUserRole, link });

						this.back();
					} catch (e) {
						if (e.response.status === 422) {
							this.modelState = e.response.data;
							if (this.modelError()) {
								throw new Error(this.modelError());
							}
						}

						throw e;
					} finally {
						this.loading = false;
					}
				}
			}
			this.loading = false;
		},
		back() {
			this.$router.go(-1);
		},
		async getRoles() {
			this.roleLoading = true;
			try {
				const newrolerequest = {
					id: this.organization.selected,
					userId: this.user.selected,
				};
				this.role.selected = [];
				this.role.items = [];
				this.role.items = await this.GET_ORGANIZATION_ROLES(newrolerequest);

				if (this.role.items.length === 1) {
					this.role.selected.push(this.role.items[0].value);
				}
			} finally {
				this.roleLoading = false;
			}
		},
		async getOrganizations() {
			this.orgLoading = true;
			try {
				const possibleBranchesRequest = {
					id: this.Organization,
					userId: this.user.selected,
				};

				this.organization.items = await this.GET_BRANCHES(possibleBranchesRequest);

				if (this.organization.items.length === 1) {
					this.organization.selected = this.organization.items[0].value;
				}
			} finally {
				this.orgLoading = false;
			}
		},
		async getUsers() {
			this.userLoading = true;
			try {
				this.user.items = await this.GET_USERS();

				if (this.user.items.length === 1) {
					this.user.selected = this.user.items[0].value;
				}
			} finally {
				this.userLoading = false;
			}
		},
	},
};
</script>
